import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {defineMessages, injectIntl, intlShape} from 'react-intl';
import styles from './example-library.css';
import {closeExampleLibrary} from '../reducers/modals';

const EXAMPLE_INDEX_URL = 'https://cdn.kidsblock.cc/examples/index.json';

const messages = defineMessages({
    title: {
        id: 'gui.exampleLibrary.title',
        defaultMessage: 'Example Code',
        description: 'Title of the example code library modal'
    },
    searchPlaceholder: {
        id: 'gui.exampleLibrary.searchPlaceholder',
        defaultMessage: 'Search by SKU or product name',
        description: 'Placeholder text for the search input'
    },
    loadButton: {
        id: 'gui.exampleLibrary.loadButton',
        defaultMessage: 'Load',
        description: 'Button to load an example'
    },
    loading: {
        id: 'gui.exampleLibrary.loading',
        defaultMessage: 'Loading...',
        description: 'Loading state text'
    },
    loadError: {
        id: 'gui.exampleLibrary.loadError',
        defaultMessage: 'Failed to load example list. Please check your network connection.',
        description: 'Error message when index fails to load'
    },
    noResults: {
        id: 'gui.exampleLibrary.noResults',
        defaultMessage: 'No products found. Try a different keyword.',
        description: 'No search results message'
    },
    loadingCode: {
        id: 'gui.exampleLibrary.loadingCode',
        defaultMessage: 'Loading code...',
        description: 'Loading code state text'
    },
    loadCodeError: {
        id: 'gui.exampleLibrary.loadCodeError',
        defaultMessage: 'Failed to load the example. Please try again.',
        description: 'Error message when loading a specific example fails'
    },
    exampleCount: {
        id: 'gui.exampleLibrary.exampleCount',
        defaultMessage: '{count} examples',
        description: 'Number of examples shown for a product'
    },
    refresh: {
        id: 'gui.exampleLibrary.refresh',
        defaultMessage: 'Refresh',
        description: 'Tooltip for refresh button'
    }
});

class ExampleLibrary extends React.PureComponent {
    constructor (props) {
        super(props);
        this.state = {
            searchQuery: '',
            products: [],
            filteredProducts: [],
            expandedSku: null,
            loading: true,
            loadError: false,
            loadingFile: null,
            loadCodeError: false
        };
        this.handleSearchChange = this.handleSearchChange.bind(this);
        this.handleToggleProduct = this.handleToggleProduct.bind(this);
        this.handleLoadExample = this.handleLoadExample.bind(this);
        this.handleClose = this.handleClose.bind(this);
        this.handleOverlayClick = this.handleOverlayClick.bind(this);
        this.handleRefresh = this.handleRefresh.bind(this);
    }

    componentDidMount () {
        this.loadIndex();
    }

    handleRefresh () {
        this.setState({searchQuery: '', expandedSku: null});
        this.loadIndex(true);
    }

    loadIndex (forceRefresh) {
        this.setState({loading: true, loadError: false});
        const url = forceRefresh
            ? `${EXAMPLE_INDEX_URL}?t=${Date.now()}`
            : EXAMPLE_INDEX_URL;
        fetch(url)
            .then(res => {
                if (!res.ok) throw new Error('Network error');
                return res.json();
            })
            .then(data => {
                this.setState({
                    products: data,
                    filteredProducts: data,
                    loading: false
                });
            })
            .catch(() => {
                this.setState({loading: false, loadError: true});
            });
    }

    handleSearchChange (e) {
        const query = e.target.value;
        const {products} = this.state;
        const lower = query.toLowerCase();
        const filtered = products.filter(p =>
            p.sku.toLowerCase().includes(lower) ||
            (p.name && p.name.toLowerCase().includes(lower))
        );
        this.setState({
            searchQuery: query,
            filteredProducts: filtered,
            expandedSku: null
        });
    }

    handleToggleProduct (sku) {
        this.setState(prev => ({
            expandedSku: prev.expandedSku === sku ? null : sku
        }));
    }

    handleLoadExample (fileUrl, title) {
        this.setState({loadingFile: fileUrl, loadCodeError: false});
        fetch(fileUrl)
            .then(res => {
                if (!res.ok) throw new Error('Network error');
                return res.arrayBuffer();
            })
            .then(buffer => {
                this.setState({loadingFile: null});
                // loadProject may throw device-related errors after success, ignore them
                this.props.vm.loadProject(buffer).catch(() => {});
                this.props.onRequestClose();
            })
            .catch(() => {
                this.setState({loadingFile: null, loadCodeError: true});
            });
    }

    handleClose () {
        this.props.onRequestClose();
    }

    handleOverlayClick (e) {
        if (e.target === e.currentTarget) {
            this.handleClose();
        }
    }

    render () {
        const {intl} = this.props;
        const {
            searchQuery,
            filteredProducts,
            expandedSku,
            loading,
            loadError,
            loadingFile,
            loadCodeError
        } = this.state;

        return (
            <div
                className={styles.overlay}
                onClick={this.handleOverlayClick}
            >
                <div className={styles.modal}>
                    {/* 标题栏 */}
                    <div className={styles.header}>
                        <span className={styles.headerIcon}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <polyline points="14 2 14 8 20 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <line x1="16" y1="13" x2="8" y2="13" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                                <line x1="16" y1="17" x2="8" y2="17" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                                <polyline points="10 9 9 9 8 9" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                        </span>
                        <span className={styles.headerTitle}>
                            {intl.formatMessage(messages.title)}
                        </span>
                        <button
                            className={styles.refreshButton}
                            onClick={this.handleRefresh}
                            title={intl.formatMessage(messages.refresh)}
                            disabled={loading}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <polyline points="23 4 23 10 17 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                        <button
                            className={styles.closeButton}
                            onClick={this.handleClose}
                        >
                            {'×'}
                        </button>
                    </div>

                    {/* 搜索栏 */}
                    <div className={styles.searchBar}>
                        <span className={styles.searchIcon}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <circle cx="11" cy="11" r="8" stroke="#999" strokeWidth="2"/>
                                <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="#999" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                        </span>
                        <input
                            className={styles.searchInput}
                            type="text"
                            value={searchQuery}
                            placeholder={intl.formatMessage(messages.searchPlaceholder)}
                            onChange={this.handleSearchChange}
                        />
                    </div>

                    {/* 内容区 */}
                    <div className={styles.content}>
                        {loading ? (
                            <div className={styles.stateMessage}>
                                <div className={styles.spinner} />
                                <span>{intl.formatMessage(messages.loading)}</span>
                            </div>
                        ) : loadError ? (
                            <div className={styles.stateMessage}>
                                <span className={styles.errorText}>
                                    {intl.formatMessage(messages.loadError)}
                                </span>
                            </div>
                        ) : filteredProducts.length === 0 ? (
                            <div className={styles.stateMessage}>
                                <span className={styles.emptyText}>
                                    {intl.formatMessage(messages.noResults)}
                                </span>
                            </div>
                        ) : (
                            <div className={styles.productList}>
                                {filteredProducts.map(product => (
                                    <div
                                        key={product.sku}
                                        className={styles.productCard}
                                    >
                                        {/* 产品行 */}
                                        <div
                                            className={styles.productRow}
                                            onClick={() => this.handleToggleProduct(product.sku)}
                                        >
                                            <div className={styles.productInfo}>
                                                <span className={styles.productSku}>{product.sku}</span>
                                                <span className={styles.productName}>{product.name}</span>
                                                <span className={styles.exampleCount}>
                                                    {this.props.intl.formatMessage(messages.exampleCount, {count: product.examples ? product.examples.length : 0})}
                                                </span>
                                            </div>
                                            <span className={`${styles.arrow} ${expandedSku === product.sku ? styles.arrowExpanded : ''}`}>
                                                {'▶'}
                                            </span>
                                        </div>

                                        {/* 示例代码列表 */}
                                        {expandedSku === product.sku && product.examples && (
                                            <div className={styles.exampleList}>
                                                {product.examples.map((example, idx) => (
                                                    <div
                                                        key={idx}
                                                        className={styles.exampleRow}
                                                    >
                                                        <span className={styles.exampleIndex}>{idx + 1}</span>
                                                        <span className={styles.exampleTitle}>{example.title}</span>
                                                        <button
                                                            className={styles.loadButton}
                                                            disabled={loadingFile === example.file}
                                                            onClick={() => this.handleLoadExample(example.file, example.title)}
                                                        >
                                                            {loadingFile === example.file
                                                                ? intl.formatMessage(messages.loadingCode)
                                                                : intl.formatMessage(messages.loadButton)
                                                            }
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* 加载代码错误提示 */}
                        {loadCodeError && (
                            <div className={styles.loadCodeError}>
                                {intl.formatMessage(messages.loadCodeError)}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

ExampleLibrary.propTypes = {
    intl: intlShape.isRequired,
    vm: PropTypes.object.isRequired,
    onRequestClose: PropTypes.func.isRequired
};

const mapStateToProps = () => ({});

const mapDispatchToProps = dispatch => ({
    onRequestClose: () => dispatch(closeExampleLibrary())
});

export default injectIntl(connect(
    mapStateToProps,
    mapDispatchToProps
)(ExampleLibrary));
